import { AuthSplitLayout } from '@/components/auth/AuthSplitLayout';
import { PhoneAuthStepPanel } from '@/components/auth/PhoneAuthStepPanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { refreshAuthProfileCache } from '@/hooks/queries/auth.queries';
import { useAuth } from '@/hooks/useAuth';
import { usePhoneAuthFlow } from '@/hooks/usePhoneAuthFlow';
import { usePhoneVerification } from '@/hooks/usePhoneVerification';
import { getApiErrorMessage } from '@/lib/api/error';
import { getPhoneNumberDigits } from '@/lib/phoneNumber';
import { AuthService } from '@/services/auth.service';
import type { AuthLoginResponseDto } from '@/types/dto/auth.dto';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

type PhoneAuthPageVariant = 'login' | 'register';

interface PhoneAuthPageProps {
  variant: PhoneAuthPageVariant;
}

const PAGE_COPY: Record<
  PhoneAuthPageVariant,
  {
    cardDescription: string;
    cardTitle: string;
    footerHref: string;
    footerLabel: string;
    footerPrefix: string;
    heroBenefits: string[];
    heroHighlight: string;
    heroTitle: string;
    mobileSubtitle: string;
  }
> = {
  login: {
    cardDescription: 'Use seu telefone',
    cardTitle: 'Entrar',
    footerHref: '/cadastro',
    footerLabel: 'Criar conta',
    footerPrefix: 'Primeira vez aqui?',
    heroBenefits: ['Encontre locais perto de você', 'Agende em poucos toques', 'Receba lembretes automáticos'],
    heroHighlight: 'em segundos',
    heroTitle: 'Encontre um horário',
    mobileSubtitle: 'Use seu telefone',
  },
  register: {
    cardDescription: 'Use seu telefone',
    cardTitle: 'Criar conta',
    footerHref: '/entrar',
    footerLabel: 'Entrar',
    footerPrefix: 'Já tem conta?',
    heroBenefits: ['Salve seus locais favoritos', 'Acompanhe seus agendamentos', 'Gerencie tudo em um só lugar'],
    heroHighlight: 'com seu telefone',
    heroTitle: 'Crie sua conta',
    mobileSubtitle: 'Use seu telefone',
  },
};

function getLocationRedirectPath(locationState: unknown) {
  if (
    typeof locationState === 'object' &&
    locationState !== null &&
    'from' in locationState &&
    typeof locationState.from === 'string'
  ) {
    return locationState.from;
  }

  return '/perfil';
}

async function updateSignedUserName(
  queryClient: ReturnType<typeof useQueryClient>,
  user: AuthLoginResponseDto['user'],
  name: string,
  phoneNumber: string,
) {
  const updatedUser = await AuthService.updateProfile({
    fullName: name,
    phoneNumber: getPhoneNumberDigits(phoneNumber) || user.phoneNumber || undefined,
  });

  await refreshAuthProfileCache(queryClient, updatedUser ?? user);
}

export function PhoneAuthPage({ variant }: PhoneAuthPageProps) {
  const pageCopy = PAGE_COPY[variant];
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setVerificationToken, signIn } = useAuth();
  const phoneAuthFlow = usePhoneAuthFlow({ storageKey: `phone-auth:${variant}` });
  const phoneVerification = usePhoneVerification({
    phoneNumber: phoneAuthFlow.phoneNumber,
    verificationCode: phoneAuthFlow.verificationCode,
  });
  const authenticatedUserRef = useRef<AuthLoginResponseDto['user'] | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isCompletingProfile, setIsCompletingProfile] = useState(false);
  const redirectPath = getLocationRedirectPath(location.state);

  const handleRequestCode = async (method: 'whatsapp' | 'sms') => {
    const hasRequestedCode = await phoneVerification.requestCode(method);

    if (!hasRequestedCode) {
      return;
    }

    phoneAuthFlow.setVerificationCode('');
    phoneAuthFlow.setAuthStep('code');
  };

  const handleValidateCode = async () => {
    const validationResponse = await phoneVerification.validateCode();

    if (!validationResponse) {
      return;
    }

    setVerificationToken(validationResponse.verificationToken);
    setIsSigningIn(true);

    try {
      const signInResponse = await signIn({
        verificationToken: validationResponse.verificationToken,
      });
      const mustCompleteProfile =
        !validationResponse.hasLinkedUser ||
        Boolean(validationResponse.requiresProfileCompletion) ||
        signInResponse.requiresProfileCompletion;

      authenticatedUserRef.current = signInResponse.user;

      if (mustCompleteProfile) {
        phoneAuthFlow.setUserName(signInResponse.user.name?.trim() || phoneAuthFlow.userName);
        phoneAuthFlow.setAuthStep('name');
        return;
      }

      phoneAuthFlow.reset();
      navigate(redirectPath, { replace: true });
    } catch (error) {
      toast.error('Não foi possível entrar', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleCompleteProfile = async () => {
    const trimmedUserName = phoneAuthFlow.userName.trim();

    const authenticatedUser = authenticatedUserRef.current;

    if (!authenticatedUser || !trimmedUserName || isCompletingProfile) {
      return;
    }

    setIsCompletingProfile(true);

    try {
      await updateSignedUserName(queryClient, authenticatedUser, trimmedUserName, phoneAuthFlow.phoneNumber);
      phoneAuthFlow.reset();
      navigate('/perfil', { replace: true });
    } catch (error) {
      toast.error('Não foi possível concluir seu cadastro', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    } finally {
      setIsCompletingProfile(false);
    }
  };

  return (
    <AuthSplitLayout
      heroTitle={pageCopy.heroTitle}
      heroHighlight={pageCopy.heroHighlight}
      heroBenefits={pageCopy.heroBenefits}
      mobileSubtitle={pageCopy.mobileSubtitle}
    >
      <Card className="border-0 shadow-xl lg:shadow-2xl">
        <CardHeader className="pb-2 text-center">
          <CardTitle className="text-2xl font-bold">{pageCopy.cardTitle}</CardTitle>
          <CardDescription className="text-base">{pageCopy.cardDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 pt-6">
          <PhoneAuthStepPanel
            authStep={phoneAuthFlow.authStep}
            isRequestingCode={phoneVerification.isRequestingCode}
            isValidatingCode={phoneVerification.isValidatingCode || isSigningIn}
            nameDescription="Seu nome para continuar"
            onAuthStepChange={phoneAuthFlow.setAuthStep}
            onPhoneNumberChange={phoneAuthFlow.setPhoneNumber}
            onRequestCode={handleRequestCode}
            onUserNameChange={phoneAuthFlow.setUserName}
            onValidateCode={() => void handleValidateCode()}
            onVerificationCodeChange={phoneAuthFlow.setVerificationCode}
            phoneNumber={phoneAuthFlow.phoneNumber}
            phoneRequestMethod={phoneVerification.requestedMethod}
            userName={phoneAuthFlow.userName}
            verificationCode={phoneAuthFlow.verificationCode}
          />

          {phoneAuthFlow.authStep === 'name' && (
            <Button
              type="button"
              className="h-12 w-full text-base font-semibold"
              disabled={phoneAuthFlow.userName.trim().length === 0 || isCompletingProfile}
              onClick={() => void handleCompleteProfile()}
            >
              {isCompletingProfile ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Continuando...
                </>
              ) : (
                'Continuar'
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      <p className="text-muted-foreground mt-6 text-center text-sm">
        {pageCopy.footerPrefix}{' '}
        <Link to={pageCopy.footerHref} className="text-primary font-semibold hover:underline">
          {pageCopy.footerLabel}
        </Link>
      </p>
    </AuthSplitLayout>
  );
}
